import * as PIXI from 'pixi.js';

import { assertBoolean } from '../../validators';
import { Config } from '../../video-data/config';

import type { CreationType, DynamicStylingCreationLevel } from '../../enums';
import type { ParentMixinSettings } from '../parentMixin';
import { ParentMixin } from '../parentMixin';
import type { UpdateFlowMixinSettings } from '../updateFlowMixin';
import { UpdateFlowMixin } from '../updateFlowMixin';
import type { SerializableMixinSettings } from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import type { DynamicStylingMixinRequirements } from '../dynamicStylingMixin';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import {
    AlphaAttribute,
    BooleanAttribute,
    ColorAttribute,
    FontAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize
} from '../attributes';

import { WordStyle } from './WordStyle';

type TextAssetStyleSettings = ParentMixinSettings & UpdateFlowMixinSettings & SerializableMixinSettings;

type GetCreationContainersFuncType = (level: DynamicStylingCreationLevel, type: CreationType) => PIXI.Container[];

export class TextAssetStyle extends DynamicStylingMixin(UpdateFlowMixin(ParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter)))) implements DynamicStylingMixinRequirements {
    override _classType = 'TextAssetStyle';

    #getCreationContainersFunc: GetCreationContainersFuncType = null;
    #destroyed = false;
    protected _requiresRedraw = false;

    // Note that non serialized properties are either transient, or are 'global' to styles and serialized in TextAsset.
    @NumberAttribute({ serialize: false, animatable: false })
    fontSize = 26;
    @FontAttribute()
    fontUrl = Config.DEFAULT_FONT_URL;
    @NumberAttribute({ serialize: false, animatable: false })
    leading = 0;
    @NumberAttribute({ serialize: false, animatable: false })
    maxTextHeight = Infinity;
    @StringAttribute({ choices: Object.values(Config.READING_DIRECTION), serialize: false })
    readingDirection = Config.READING_DIRECTION.LTR;
    @BooleanAttribute()
    /**
     * Hint to the TextAsset that if true, render the highlighted elements into this layer.
     */
    renderHighlighted = false;
    @BooleanAttribute()
    /**
     * Hint to the TextAsset that if true, render the highlighted elements with the highlighted color instead of the normal color.
     */
    renderHighlightedColor = false;
    @StringAttribute({ choices: Object.values(Config.TEXT_ALIGN_HORIZONTAL) })
    textAlign = Config.TEXT_ALIGN_HORIZONTAL.LEFT;
    // Never serialized - used to show/hide text programmatically in UI code.
    @AlphaAttribute({ serialize: false })
    textAlpha = 1;
    @ColorAttribute()
    textColor: number = 0;
    @BooleanAttribute()
    textShadow = true;
    @AlphaAttribute()
    textShadowAlpha = 0.5;
    @NumberAttribute({ animatable: false })
    textShadowAngle = 1;
    @NumberAttribute({ animatable: false })
    textShadowBlur = 10;
    @ColorAttribute()
    textShadowColor = 0;
    @NumberAttribute({ animatable: false })
    textShadowDistance = 2;

    constructor(getCreationContainersFunc: GetCreationContainersFuncType, settings: TextAssetStyleSettings) {
        super(settings);

        this.#getCreationContainersFunc = getCreationContainersFunc;
    }

    get destroyed() {
        return this.#destroyed;
    }

    destroy() {
        this.#destroyed = true;
        this.destroyDynamicStyles();
    }

    childRequestingRedraw(child) {
        // Forward to the TextAsset
        this.parent.childRequestingRedraw(child);
    }

    get animationManager() {
        return this.parent.animationManager;
    }

    /**
     * Creates a WordStyle from this TextAssetStyle.
     */
    createWordStyle(): WordStyle {
        const wordStyle = new WordStyle();
        wordStyle.align = this.textAlign;
        wordStyle.dropShadow = this.textShadow;
        wordStyle.dropShadowAlpha = this.textShadowAlpha;
        wordStyle.dropShadowAngle = this.textShadowAngle;
        wordStyle.dropShadowBlur = this.textShadowBlur;
        wordStyle.dropShadowColor = this.textShadowColor;
        wordStyle.dropShadowDistance = this.textShadowDistance;
        wordStyle.fill = this.textColor;
        wordStyle.fontSize = this.fontSize;
        wordStyle.fontUrl = this.fontUrl;
        wordStyle.leading = this.leading;
        wordStyle.maxTextHeight = this.maxTextHeight;
        wordStyle.readingDirection = this.readingDirection;

        return wordStyle;
    }

    /**
     * Set whether or not the given changes requires us to redraw the object. If for example we change the color or size
     * of the object then we need to pass on a value of True. This will then redraw the object with the new color or size.
     */
    set requiresRedraw(value: boolean) {
        assertBoolean(value);
        this._requiresRedraw = value;
        if (value) {
            this.requiresUpdate = true;
        }
    }

    override get animateOutBehaviour() {
        return this.parent.animateOutBehaviour;
    }

    /**
     * @inheritdoc
     */
    override get animationAnchorsToRemove() {
        return this.parent.animationAnchorsToRemove;
    }

    /**
     * Returns whether or not the object has changes that need to be drawn.
     */
    get requiresRedraw(): boolean {
        return this._requiresRedraw;
    }

    /**
     * @inheritDoc
     */
    override getCreationContainersAtLevelForType(level: DynamicStylingCreationLevel, type: CreationType): PIXI.Container[] {
        return this.#getCreationContainersFunc(level, type);
    }

    override get readonlyProperties() {
        return [];
    }

    override get propertiesToSerialize() {
        // `animateOutBehaviour` is applied to both styles from the setting in the `TextAsset`.
        const dynamicStylingProperties = this.dynamicStylingPropertiesToSerialize.filter(p => p !== 'animateOutBehaviour');
        return getAttributesToSerialize(this).concat(dynamicStylingProperties);
    };

    override get editableProperties() {
        return super.editableProperties.concat([
            'fontUrl',
            'readingDirection',
            'textColor',
            'textShadowColor',
            'colorGraphics',
            'alphaGraphics',
        ]);
    }

    clear() {
        this.destroyDynamicStyles();
    }
}
